import { createContext, type ReactNode, useContext } from "react";
import type { Components } from "react-markdown";

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const childrenOf = (value: unknown): unknown[] => {
	if (!isRecord(value)) return [];
	return Array.isArray(value.children) ? value.children : [];
};

const tagNameOf = (value: unknown): string | undefined => {
	if (!isRecord(value)) return undefined;
	return typeof value.tagName === "string" ? value.tagName : undefined;
};

/** Cells in the table's first row, whatever depth `thead`/`tbody` puts it at. */
const firstRowCellCount = (node: unknown): number => {
	const queue: unknown[] = [node];
	while (queue.length > 0) {
		const current = queue.shift();
		if (tagNameOf(current) === "tr") {
			return childrenOf(current).filter((cell) => {
				const tag = tagNameOf(cell);
				return tag === "th" || tag === "td";
			}).length;
		}
		queue.push(...childrenOf(current));
	}
	return 0;
};

type TableLayout = "table" | "keyValue";
type TableSection = "head" | "body";

const TableContext = createContext<TableLayout>("table");
const SectionContext = createContext<TableSection>("body");

/**
 * A two-column table is a metric list, not a grid — the agent reaches for one whenever it pulls
 * numbers out of prose. Rendering it as label/value rows keeps the figures on a mono, tabular
 * baseline so they line up down the column and read as values rather than sentence fragments.
 */
const KEY_VALUE_ROW =
	"grid grid-cols-[1fr_auto] items-baseline gap-3 py-[7px] [&>*:last-child]:text-right";
const KEY_VALUE_BODY_ROW =
	"[&>*:first-child]:text-[12px] [&>*:first-child]:font-medium [&>*:first-child]:leading-[18px] [&>*:first-child]:text-tertiary [&>*:last-child]:font-mono [&>*:last-child]:text-[13px] [&>*:last-child]:font-semibold [&>*:last-child]:leading-[18px] [&>*:last-child]:tabular-nums [&>*:last-child]:text-primary";
const KEY_VALUE_HEAD_ROW =
	"[&>*]:text-[11px] [&>*]:font-semibold [&>*]:uppercase [&>*]:leading-[16px] [&>*]:tracking-[0.6px] [&>*]:text-tertiary";

const TableSectionWrapper = ({
	children,
	section,
}: {
	children?: ReactNode;
	section: TableSection;
}) => {
	const layout = useContext(TableContext);
	if (layout !== "keyValue") {
		return section === "head" ? <thead>{children}</thead> : <tbody>{children}</tbody>;
	}
	return (
		<SectionContext.Provider value={section}>
			<div
				className={
					section === "head"
						? "border-b border-bc-subtle"
						: "flex flex-col divide-y divide-bc-subtle"
				}
			>
				{children}
			</div>
		</SectionContext.Provider>
	);
};

const TableRow = ({ children }: { children?: ReactNode }) => {
	const layout = useContext(TableContext);
	const section = useContext(SectionContext);
	if (layout !== "keyValue") return <tr>{children}</tr>;
	return (
		<div
			className={`${KEY_VALUE_ROW} ${section === "head" ? KEY_VALUE_HEAD_ROW : KEY_VALUE_BODY_ROW}`}
		>
			{children}
		</div>
	);
};

const TableCell = ({ children, kind }: { children?: ReactNode; kind: "td" | "th" }) => {
	const layout = useContext(TableContext);
	if (layout === "keyValue") return <span className="min-w-0">{children}</span>;
	if (kind === "th") {
		return <th className="border border-bc-subtle px-1.5 py-1 font-semibold">{children}</th>;
	}
	return <td className="border border-bc-subtle px-1.5 py-1">{children}</td>;
};

/** Table renderers for `AdminAssistantMarkdown`; a two-column table renders as label/value rows. */
export const tableComponents: Components = {
	table: ({ children, node }) => {
		const layout: TableLayout = firstRowCellCount(node) === 2 ? "keyValue" : "table";
		if (layout === "keyValue") {
			return (
				<TableContext.Provider value={layout}>
					<div className="flex flex-col border-t border-bc-default" data-testid="assistant-metrics">
						{children}
					</div>
				</TableContext.Provider>
			);
		}
		return (
			<TableContext.Provider value={layout}>
				<div className="overflow-x-auto">
					<table className="w-full border-collapse text-left">{children}</table>
				</div>
			</TableContext.Provider>
		);
	},
	thead: ({ children }) => <TableSectionWrapper section="head">{children}</TableSectionWrapper>,
	tbody: ({ children }) => <TableSectionWrapper section="body">{children}</TableSectionWrapper>,
	tr: ({ children }) => <TableRow>{children}</TableRow>,
	td: ({ children }) => <TableCell kind="td">{children}</TableCell>,
	th: ({ children }) => <TableCell kind="th">{children}</TableCell>,
};
